import React, { useState, useMemo, useEffect } from 'react'
import { FiChevronUp, FiChevronDown } from 'react-icons/fi'
import { Collapse, CardBody, CardHeader } from 'reactstrap'

import { Button } from '../Buttons'
import Loader from '../Loader'

import * as S from './styles'

const Panel = ({
  template,
  title,
  description,
  headerSideContent,
  noPaddingBody,
  children,
  isLoading,
  isCollapsible,
  onCollapse,
  initialCollapsibleState = true,
  className,
  iconLeft,
  unmountContentIfCollapse = false,
  // texts collapse
  collapsibleTextOpen,
  collapsibleTextClose,
  disabled,
  // controlled Panel
  open,
  onChangeOpen,
  ...rest
}: PanelProps) => {
  const hasOpenProp = open !== undefined
  const [_isOpen, setIsOpen] = useState(
    hasOpenProp ? open : initialCollapsibleState
  )

  const isOpen = hasOpenProp ? open : _isOpen

  useEffect(() => {
    if (hasOpenProp) return
    setIsOpen(initialCollapsibleState)
  }, [initialCollapsibleState, hasOpenProp])

  const handeCollapse = () => {
    if (disabled) return
    if (hasOpenProp) {
      onChangeOpen && onChangeOpen(!open)
      return
    }
    setIsOpen(oldState => {
      onCollapse?.(!oldState)

      return !oldState
    })
  }

  const IconCollapse = useMemo(
    () => (isOpen ? FiChevronUp : FiChevronDown),
    [isOpen]
  )
  // No text next to the chevron unless a caller asks for one: the arrow
  // already says the panel opens and closes, and "Show more / Show less"
  // repeated on every card was pure noise (2026-09-15 demo, SPEC 004 item 8.1).
  const textCollapse = useMemo(
    () => (isOpen ? collapsibleTextClose : collapsibleTextOpen),
    [collapsibleTextClose, collapsibleTextOpen, isOpen]
  )

  return (
    <S.Container
      className={`panel ${className || ''}`}
      template={template}
      disabled={disabled}
      {...rest}
    >
      {(title || description || headerSideContent) && (
        <CardHeader
          {...(isCollapsible ? { onClick: handeCollapse, role: 'button' } : {})}
        >
          {iconLeft && (
            <div className="panel__header-icon__left">{iconLeft}</div>
          )}

          <div className="panel__header-heading">
            {!!title && <strong className="title">{title}</strong>}

            {!!description && (
              <span className="description">{description}</span>
            )}
          </div>
          <div className="panel__header-side-content">
            {headerSideContent}

            {!!isCollapsible && (
              <Button
                template="transparentPrimary"
                customWidth="auto"
                iconRight={<IconCollapse />}
                as="span"
                size="xsmall"
                text={textCollapse}
                style={{ whiteSpace: 'nowrap' }}
              />
            )}
          </div>
        </CardHeader>
      )}
      <Collapse isOpen={isOpen}>
        {unmountContentIfCollapse && !isOpen ? (
          <></>
        ) : (
          <CardBody className={noPaddingBody ? 'no-padding' : ''}>
            {isLoading ? <Loader /> : children}
          </CardBody>
        )}
      </Collapse>
    </S.Container>
  )
}

export default Panel

Panel.defaultProps = {
  title: null,
  template: 'default',
  description: null,
  headerSideContent: null,
  noPaddingBody: false,
  initialCollapsibleState: true,
  children: null,
  isLoading: false,
  isCollapsible: false,
  className: null
}
